import React, { useState, useEffect } from "react";
import { Row, Col, Card, Tab, Nav, Modal, Button, Spinner, Form, Alert } from "react-bootstrap";
import { Information, Add, Trash } from "iconsax-react";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import MyApiClient from "../../../../../api-client/MyApiClient";
import Select from "react-select";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { htmlVuoto, testoInHtml } from "../../../../components/Util/testoInHtml";
import { sidSelectStyles } from "../../../../components/Util/sidSelectStyles";
import { sports, tipologiaStruttura } from "../../../../components/Util/enum";
import { useProfile } from "../../../../../hooks/useProfile";
import { useMemberships } from "../../../../../hooks/useMemberships";
import ProductImageUploader from "../../../../components/Util/ProductImageUploader";
import { VAT_OPTIONS, findVatOption, vatFieldsFromOption } from "../../../../components/Util/vatOptions";
import { conMotivo } from "../../../../components/Util/erroreServer";
import { usePageTitle } from "../../../../layouts/PageHeaderExtra";
import { useStatoNelTitolo } from "../../../../components/Util/StatoProdotto";
import AvvisoFatturazione from '../../../../components/Util/AvvisoFatturazione';
import { segnalaMancanti } from '../../../../components/Util/campiMancanti';
import chiediConferma from '../../../../components/Util/chiediConferma';
import { NOTA_NON_IN_APP } from '../../../../components/Util/AvvisoInLista';
import { CampoSport, sportDelProdotto, sportDaSalvare } from '../../../../components/Util/sportProdotto';

/*
 * Nuova lezione e bozza di una lezione: una pagina sola, con lo stesso aspetto della scheda (view.js):
 * un box a sezioni (Informazioni, Prezzi, Forme di pagamento, Programmazione). Prima era un percorso a
 * cinque passi (Informazioni -> Prezzi -> Condizioni -> Programmazione -> Riepilogo).
 * Senza id nell'indirizzo crea la lezione (una per maestro scelto); con l'id (lessonseditmultiple/:id,
 * con ?ids= per le lezioni dello stesso gruppo) apre la bozza. Una lezione non in bozza si apre nella scheda.
 * "Salva bozza" chiede solo il nome; "Salva e attiva" fa tutti i controlli del vecchio percorso insieme.
 */
const dayOptions = [
    { value: 'MONDAY', label: 'Lunedì' },
    { value: 'TUESDAY', label: 'Martedì' },
    { value: 'WEDNESDAY', label: 'Mercoledì' },
    { value: 'THURSDAY', label: 'Giovedì' },
    { value: 'FRIDAY', label: 'Venerdì' },
    { value: 'SATURDAY', label: 'Sabato' },
    { value: 'SUNDAY', label: 'Domenica' }
];

export const LessonsNew = () => {

    const navigate = useNavigate();
    const { id } = useParams();
    const { search } = useLocation();
    const bozzaEsistente = !!id;
    const idsParam = new URLSearchParams(search).get('ids');

    const { profile: profileData } = useProfile({
        autoFetch: true,
        includeTitles: true
    });
    const { getConfirmedMembers, memberships, loading: membershipsLoading } = useMemberships({ autoFetch: true });

    const sdiEnabledNoVat = profileData?.club?.invoiceDataSender?.invoice_enabled === true && !profileData?.club?.invoiceDataSender?.vat_number;
    const billingNotConfirmed = profileData && profileData?.club?.invoiceDataSender?.status !== 'CONFIRMED' && profileData?.club?.profileStatus !== 'INACTIVE';
    const prezzoBloccato = sdiEnabledNoVat || billingNotConfirmed;

    const [loadingShowed, setLoadingShowed] = useState(false);


    const [pictureModalShowed, setPictureModalShowed] = useState(false);

    const [enabledSports, setEnabledSports] = useState([]);
    const [availableTeachers, setAvailableTeachers] = useState([]);

    // Bozza esistente: le lezioni del gruppo (una per maestro) e il gruppo
    const [groupedLessonsData, setGroupedLessonsData] = useState([]);
    const [groupId, setGroupId] = useState('');

    const [modalShowed, setModalShowed] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalText, setModalText] = useState('');

    const [showingSociOptions, setShowingSociOptions] = useState(false);
    const [showingTesseratiOptions, setShowingTesseratiOptions] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState({
        stripe_enabled: false,
        bank_transfer_enabled: false
    });

    const [formData, setFormData] = useState({
        name: '',
        sportTypes: [],
        description: '',
        standard_price: 0,
        member_price: 0,
        federated_price: 0,
        fitcoin_cashback: 0,
        payable_in_club: false,
        payable_stripe: false,
        payable_bank: false,
        hidden_in_app: false,
        vat_rate: 22,
        vat_nature: undefined,
        iva_5_percent: false,
        type: 'CLOSED',
        coachId: [],
        newLessonPicture: '',
        lessonPicture: '',
        weeklySchedule: []
    });

    // Nome com'e' stato caricato: il titolo non cambia mentre lo si modifica
    const [loadedName, setLoadedName] = useState('');
    usePageTitle(bozzaEsistente ? (loadedName || null) : null);
    useStatoNelTitolo({ visibile: bozzaEsistente && !!loadedName, attivo: false, bozza: true });

    useEffect(() => {
        if (profileData && profileData.club && profileData.club.sports) {
            const clubSports = profileData.club.sports.map(s => s.sportType);
            setEnabledSports(sports.filter(element => clubSports.includes(element.value)));
        }
    }, [profileData]);

    useEffect(() => {
        const tokenDetailsString = sessionStorage.getItem('userDetails');
        if (tokenDetailsString) {
            const tokenDetails = JSON.parse(tokenDetailsString);
            if (tokenDetails.clubNature === tipologiaStruttura.ASD.value) {
                setShowingSociOptions(true);
            }
            if (tokenDetails.clubNature === tipologiaStruttura.ASD.value || tokenDetails.clubNature === tipologiaStruttura.SSD.value) {
                setShowingTesseratiOptions(true);
            }
            setPaymentMethods({
                stripe_enabled: tokenDetails.stripe_enabled || false,
                bank_transfer_enabled: tokenDetails.bank_transfer_enabled || false
            });
        }
    }, []);

    // Maestri confermati della struttura
    useEffect(() => {
        // Se l'elenco dei membri non arriva (errore del server) restano zero maestri, senza far cadere la pagina
        if (membershipsLoading || !Array.isArray(memberships)) return;
        const teachersData = getConfirmedMembers('COACH');
        if (!Array.isArray(teachersData)) return;
        setAvailableTeachers(teachersData.map(teacher => {
            const rawLabel = (
                teacher.name ||
                ((teacher.firstName || teacher.lastName) ? `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() : '') ||
                ((teacher.member && (teacher.member.firstName || teacher.member.lastName)) ? `${teacher.member.firstName || ''} ${teacher.member.lastName || ''}`.trim() : '') ||
                (teacher.username ? String(teacher.username) : '')
            );
            return {
                value: String(teacher.userId),
                label: rawLabel || 'Senza nome',
                sportType: teacher.sportType || teacher.sport || (teacher.member && teacher.member.sportType ? teacher.member.sportType : null)
            };
        }));
    }, [memberships, membershipsLoading, getConfirmedMembers]);

    // Bozza esistente: si caricano la lezione e quelle dello stesso gruppo
    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            try {
                const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
                if (!token) return;
                const apiClient = new MyApiClient();
                const groupLessonIds = idsParam ? Array.from(new Set(idsParam.split(',').filter(Boolean))) : [];

                setLoadingShowed(true);
                const lezione = await apiClient.getBookingLessonById(token, id);
                const gruppo = groupLessonIds.length > 0
                    ? await Promise.all(groupLessonIds.map(lid => apiClient.getBookingLessonById(token, lid)))
                    : [];
                setLoadingShowed(false);
                if (!lezione) return;

                // Una lezione gia' attiva o non attiva si gestisce nella sua scheda
                if (lezione.status && lezione.status !== 'DRAFT') {
                    navigate(`/struttura/booking/lessonsview/${id}${search || ''}`, { replace: true });
                    return;
                }

                setFormData({
                    id: lezione.id,
                    name: lezione.name,
                    sportTypes: sportDelProdotto(lezione),
                    description: lezione.description || '',
                    standard_price: lezione.standard_price,
                    member_price: lezione.member_price,
                    federated_price: lezione.federated_price,
                    fitcoin_cashback: lezione.fitcoin_cashback,
                    payable_in_club: lezione.payable_in_club,
                    payable_stripe: lezione.payable_stripe,
                    payable_bank: lezione.payable_bank,
                    hidden_in_app: !!lezione.hidden_in_app,
                    vat_rate: lezione.vat_rate,
                    vat_nature: lezione.vat_nature,
                    iva_5_percent: lezione.iva_5_percent,
                    type: 'CLOSED',
                    coachId: gruppo.length > 0
                        ? gruppo.map(gl => String(gl.coachId)).filter(Boolean)
                        : (lezione.coachId ? [String(lezione.coachId)] : []),
                    newLessonPicture: '',
                    lessonPicture: lezione.lessonPicture,
                    weeklySchedule: (lezione.weeklySchedule || []).map((o, i) => ({ ...o, id: o.id || `orario-${i}` }))
                });
                setLoadedName(lezione.name || '');
                setGroupedLessonsData(gruppo);
                const gid = lezione.groupId || gruppo[0]?.groupId;
                if (gid) setGroupId(String(gid));
            } catch (error) {
                setLoadingShowed(false);
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, idsParam]);

    // I maestri che insegnano uno degli sport della lezione
    const filteredTeachers = availableTeachers.filter(t => formData.sportTypes.includes(t.sportType));

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Cambiando gli sport restano scelti solo i maestri di quegli sport
    const cambiaSport = (elenco) => {
        const ammessi = new Set(availableTeachers.filter(t => elenco.includes(t.sportType)).map(t => t.value));
        setFormData(prev => ({
            ...prev,
            sportTypes: elenco,
            coachId: (prev.coachId || []).map(String).filter(cid => ammessi.has(cid))
        }));
    };

    const cambiaMaestro = (cid, scelto) => {
        setFormData(prev => {
            const attuali = Array.isArray(prev.coachId) ? prev.coachId.map(String) : [];
            const coachId = scelto ? Array.from(new Set([...attuali, cid])) : attuali.filter(v => v !== cid);
            return { ...prev, coachId };
        });
    };

    // Programmazione settimanale
    const aggiungiOrario = () => {
        setFormData(prev => ({
            ...prev,
            weeklySchedule: [...prev.weeklySchedule, { id: Date.now().toString(), dayOfWeek: '', startTime: '', endTime: '' }]
        }));
    };

    const cambiaOrario = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            weeklySchedule: prev.weeklySchedule.map((item, i) => (i === index ? { ...item, [field]: value } : item))
        }));
    };

    const rimuoviOrario = (index) => {
        setFormData(prev => ({ ...prev, weeklySchedule: prev.weeklySchedule.filter((_, i) => i !== index) }));
    };

    const mostraErrore = (titolo, testo) => {
        setModalTitle(titolo);
        setModalText(testo);
        setModalShowed(true);
    };

    // La lezione come la vuole il server, sempre in bozza: l'attivazione e' una chiamata a parte.
    const payloadBozza = () => {
        const tokenDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
        const payload = {
            name: formData.name,
            ...sportDaSalvare(formData.sportTypes),
            clubId: tokenDetails.localId,
            description: formData.description,
            standard_price: formData.standard_price,
            member_price: formData.member_price,
            federated_price: formData.federated_price,
            fitcoin_cashback: formData.fitcoin_cashback,
            payable_in_club: formData.payable_in_club,
            payable_stripe: formData.payable_stripe,
            payable_bank: formData.payable_bank,
            hidden_in_app: formData.hidden_in_app,
            vat_rate: formData.vat_rate,
            vat_nature: formData.vat_nature,
            iva_5_percent: formData.iva_5_percent,
            status: 'DRAFT',
            type: formData.type || 'CLOSED',
            weeklySchedule: formData.weeklySchedule.map(o => ({ dayOfWeek: o.dayOfWeek, startTime: o.startTime, endTime: o.endTime }))
        };
        if (!showingSociOptions) {
            payload.member_price = payload.standard_price;
        }
        if (!showingTesseratiOptions) {
            payload.federated_price = payload.standard_price;
        }
        Object.keys(payload).forEach(key => { if (payload[key] === '') delete payload[key]; });
        return payload;
    };

    // L'immagine nuova viaggia come { content, name }
    const nuovaFoto = () => (formData.newLessonPicture
        ? {
            content: formData.newLessonPicture,
            name: Math.random().toString(36).substring(2, 10) + (new Date()).getTime().toString(36),
        }
        : undefined);

    const maestriScelti = () => (Array.isArray(formData.coachId) ? formData.coachId.map(String) : []);

    // Crea (una lezione per maestro) o aggiorna la bozza. Torna { ids, groupId } per l'attivazione,
    // oppure null se non riesce (errore gia' mostrato).
    const registraBozza = async (token) => {
        const apiClient = new MyApiClient();
        const base = payloadBozza();
        const foto = nuovaFoto();
        const maestri = maestriScelti();

        if (!bozzaEsistente) {
            if (foto) base.newLessonPicture = foto;
            let result;
            if (maestri.length > 1) {
                result = await apiClient.createBookingLessonMultiple(token, maestri.map(cid => ({ ...base, coachId: cid })));
            } else {
                result = await apiClient.createBookingLesson(token, { ...base, coachId: maestri[0] || undefined });
            }
            if (!result || result.error) {
                mostraErrore("Errore durante l'operazione", result?.error || conMotivo("Si è verificato un errore durante il salvataggio"));
                return null;
            }
            const creati = Array.isArray(result.data) ? result.data : [result.data];
            return { ids: creati.map(l => l?.id).filter(Boolean), groupId: creati[0]?.groupId };
        }

        // Bozza esistente: si aggiorna il gruppo (una lezione per maestro; quelle dei maestri nuovi si aggiungono)
        const esistentiPerMaestro = new Map((groupedLessonsData || []).map(gl => [String(gl.coachId), gl]));
        const campiFoto = (cid) => {
            if (formData.lessonPicture === null) return { lessonPicture: null };
            const gl = esistentiPerMaestro.get(cid);
            const campi = { lessonPicture: gl?.lessonPicture ?? formData.lessonPicture ?? null };
            if (foto) campi.newLessonPicture = foto;
            return campi;
        };
        const gid = groupId || (groupedLessonsData[0]?.groupId ? String(groupedLessonsData[0].groupId) : '');
        let result;
        if (gid) {
            const lessons = maestri.map(cid => ({
                ...base,
                ...campiFoto(cid),
                coachId: cid,
                ...(esistentiPerMaestro.has(cid) ? { id: esistentiPerMaestro.get(cid).id } : {})
            }));
            result = await apiClient.updateBookingLessonMultiple(token, { groupId: gid, lessons });
        } else {
            // Lezione singola senza gruppo
            result = await apiClient.updateBookingLesson(token, { ...base, ...campiFoto(maestri[0]), id, coachId: maestri[0] || undefined });
        }
        if (!result || result.error) {
            mostraErrore("Errore durante l'operazione", result?.error || conMotivo("Si è verificato un errore durante il salvataggio"));
            return null;
        }
        return { ids: [id], groupId: gid };
    };

    const salvaBozza = async () => {
        // Dati mancanti: si evidenzia il campo (regola «Dati mancanti»)
        const mancanti = [];
        if (!formData.name || formData.name.trim() === '') mancanti.push({ campo: 'lez-name', messaggio: 'Manca il nome della lezione' });
        if (bozzaEsistente && maestriScelti().length === 0) mancanti.push(campoStaff());
        if (segnalaMancanti(mancanti) > 0) return;
        try {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            if (!token) return;
            setLoadingShowed(true);
            const salvato = await registraBozza(token);
            setLoadingShowed(false);
            if (salvato) navigate('/struttura/booking/lessons');
        } catch (error) {
            setLoadingShowed(false);
            console.error('Error in salvaBozza: ' + error);
            mostraErrore("Errore durante l'operazione", conMotivo("Si è verificato un errore durante il salvataggio della bozza"));
        }
    };

    // Gli stessi controlli del vecchio percorso a passi, tutti insieme: ogni campo che manca è segnalato sul campo stesso.
    const cosaManca = () => {
        const numero = (v) => parseFloat(String(v ?? '').replace(',', '.'));
        const mancanti = [];
        if (!formData.name || !formData.name.trim()) mancanti.push({ campo: 'lez-name', messaggio: 'Manca il nome della lezione' });
        if (formData.sportTypes.length === 0) mancanti.push({ campo: 'lez-sport', messaggio: 'Manca lo sport' });
        else if (maestriScelti().length === 0) mancanti.push(campoStaff());
        if (htmlVuoto(formData.description)) mancanti.push({ campo: 'lez-description', messaggio: 'Manca la descrizione della lezione' });
        if (!formData.newLessonPicture && !formData.lessonPicture) mancanti.push({ campo: 'lez-photo', messaggio: 'Manca la foto della lezione' });
        if (!prezzoBloccato && !(numero(formData.standard_price) > 0)) mancanti.push({ campo: 'lez-standard_price', messaggio: 'Il prezzo standard deve essere maggiore di 0' });
        if (showingTesseratiOptions && !(numero(formData.federated_price) > 0)) mancanti.push({ campo: 'lez-federated_price', messaggio: 'Il prezzo tesserati / soci deve essere maggiore di 0' });
        if (!(numero(formData.fitcoin_cashback) >= 0)) mancanti.push({ campo: 'lez-fitcoin_cashback', messaggio: 'Il cashback Fitcoin deve essere maggiore o uguale a 0' });
        if (!formData.payable_in_club && !formData.payable_stripe && !formData.payable_bank) {
            mancanti.push({ campo: 'switch-payable_in_club', messaggio: 'Attiva almeno una forma di pagamento: in struttura, Stripe o bonifico' });
        }
        if (formData.weeklySchedule.length === 0) {
            mancanti.push({ campo: 'lez-aggiungi-orario', messaggio: 'Aggiungi almeno un orario nella programmazione settimanale' });
        }
        formData.weeklySchedule.forEach((o, i) => {
            if (!o.dayOfWeek) mancanti.push({ campo: `giorno-${i}`, messaggio: 'Manca il giorno' });
            if (!o.startTime) mancanti.push({ campo: `inizio-${i}`, messaggio: "Manca l'ora di inizio" });
            if (!o.endTime) mancanti.push({ campo: `fine-${i}`, messaggio: "Manca l'ora di fine" });
            else if (o.startTime && o.startTime >= o.endTime) mancanti.push({ campo: `fine-${i}`, messaggio: "L'ora di fine deve essere dopo l'inizio" });
        });
        return mancanti;
    };

    // Dove si segnala che manca il maestro: sulla lista dei maestri, o sullo sport se non c'e' ancora una lista
    const campoStaff = () => (formData.sportTypes.length > 0 && filteredTeachers.length > 0
        ? { campo: 'lez-staff', messaggio: 'Scegli almeno un maestro dello staff tecnico' }
        : { campo: 'lez-sport', messaggio: formData.sportTypes.length > 0 ? 'Nessun maestro insegna questi sport: scegli un altro sport' : 'Manca lo sport: poi potrai scegliere il maestro' });

    const salvaEAttiva = () => {
        if (segnalaMancanti(cosaManca()) > 0) return;
        chiediConferma({ titolo: 'Attivare la lezione?', testo: 'Dopo l\'attivazione potrai cambiare solo nome, descrizione, prezzi e forme di pagamento: maestro e programmazione restano quelli scelti.', conferma: 'Attiva', variante: 'primary' })
            .then((ok) => { if (ok) continuaSalvaEAttiva(); });
    };

    const continuaSalvaEAttiva = async () => {
        try {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            if (!token) return;
            const apiClient = new MyApiClient();
            setLoadingShowed(true);
            const salvato = await registraBozza(token);
            if (!salvato) {
                setLoadingShowed(false);
                return;
            }
            // Il gruppo si attiva tutto insieme; senza gruppo, lezione per lezione
            const attivazioni = salvato.groupId
                ? [await apiClient.changeLessonStatus(token, null, 'PURCHASABLE', salvato.groupId)]
                : await Promise.all(salvato.ids.map(lid => apiClient.changeLessonStatus(token, lid, 'PURCHASABLE')));
            setLoadingShowed(false);
            const errore = attivazioni.find(a => !a || a.error);
            if (errore !== undefined) {
                mostraErrore("Errore durante l'attivazione", errore?.error || "La lezione è stata salvata come bozza ma non è stato possibile attivarla.");
            } else {
                navigate('/struttura/booking/lessons');
            }
        } catch (error) {
            setLoadingShowed(false);
            console.error('Error in continuaSalvaEAttiva: ' + error);
            mostraErrore("Errore durante l'operazione", conMotivo("Si è verificato un errore durante il salvataggio e attivazione"));
        }
    };

    // Eliminazione della bozza: tutte le lezioni del gruppo (una per maestro)
    const eliminaBozza = async () => {
        if (await chiediConferma({ titolo: `Eliminare la bozza${formData.name ? ` «${formData.name}»` : ' della lezione'}?`, testo: 'La bozza della lezione sparisce dalla tua struttura.', conferma: 'Elimina' })) continuaEliminaBozza();
    };

    const continuaEliminaBozza = async () => {
        try {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            if (!token) return;
            const apiClient = new MyApiClient();
            const ids = groupedLessonsData.length > 0 ? groupedLessonsData.map(gl => gl.id) : [id];
            setLoadingShowed(true);
            const esiti = [];
            for (const lid of ids) {
                esiti.push(await apiClient.deleteBookingLesson(token, lid));
            }
            setLoadingShowed(false);
            const errore = esiti.find(r => !r || r.error);
            if (errore !== undefined) {
                mostraErrore("Eliminazione non riuscita", errore?.error || conMotivo("Si è verificato un errore durante l'eliminazione"));
            } else {
                navigate('/struttura/booking/lessons');
            }
        } catch (error) {
            setLoadingShowed(false);
            console.error('Error in continuaEliminaBozza: ' + error);
            mostraErrore("Eliminazione non riuscita", conMotivo("Si è verificato un errore durante l'eliminazione"));
        }
    };

    const interruttore = (fieldName, label) => (
        <div className="sid-switch-row">
            <Form.Check
                type="switch"
                id={`switch-${fieldName}`}
                label={label}
                checked={!!formData[fieldName]}
                onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: e.target.checked }))}
            />
        </div>
    );

    const foto = formData.newLessonPicture
        ? `data:image/jpeg;base64,${formData.newLessonPicture}`
        : (formData.lessonPicture || null);

    const rimuoviFoto = () => setFormData(prev => ({ ...prev, newLessonPicture: '', lessonPicture: bozzaEsistente ? null : '' }));

    return (
        <>
            <ProductImageUploader
                show={pictureModalShowed}
                onClose={() => setPictureModalShowed(false)}
                onSave={(base64String) => {
                    setPictureModalShowed(false);
                    setFormData(prev => ({ ...prev, newLessonPicture: base64String }));
                }}
                titolo="Immagine della lezione"
            />


            <Modal className="fade" show={modalShowed} centered>
                <Modal.Header>
                    <Modal.Title>{modalTitle}</Modal.Title>
                    <Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col>{modalText}</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
                </Modal.Footer>
            </Modal>

            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            {billingNotConfirmed && (
                <AvvisoFatturazione />
            )}

            <div className="custom-tab-1">
                <Tab.Container defaultActiveKey="informazioni">
                    <Nav as="ul" className="nav-tabs" style={{ border: 'none' }}>
                        <Nav.Item as="li" className="tabMenu">
                            <Nav.Link eventKey="informazioni">Informazioni generali</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <Row className="align-items-start mt-4">
                        <div className="col-12">
                            <Tab.Content>
                                <Tab.Pane eventKey="informazioni">
                                    <Card className="sid-scheda-prodotto">
                                        <Card.Body className="sid-card-body--spaced">
                                            <Row className="g-4">
                                                <Col md={6}>
                                                    <Form.Label className="mb-2" htmlFor="lez-name">Nome lezione *</Form.Label>
                                                    <Form.Control id="lez-name" className="mt-2" type="text" name="name" value={formData.name} onChange={handleChange} />
                                                </Col>
                                                {/* Sport e staff tecnico in un riquadro evidenziato: si sceglie prima lo sport,
                                                    poi i maestri di quello sport (una lezione per ogni maestro scelto). */}
                                                <Col md={12}>
                                                    <div className="sid-scelta-staff">
                                                        <div className="sid-scelta-staff__passo">
                                                            <div className="sid-scelta-staff__titolo"><span className="sid-scelta-staff__num">1</span>Sport *</div>
                                                            <CampoSport
                                                                inputId="lez-sport"
                                                                className="sid-scelta-staff__sport"
                                                                valore={formData.sportTypes}
                                                                onChange={cambiaSport}
                                                                opzioni={enabledSports}
                                                            />
                                                        </div>
                                                        <div className="sid-scelta-staff__passo">
                                                            <div className="sid-scelta-staff__titolo">
                                                                <span className="sid-scelta-staff__num">2</span>Staff tecnico *
                                                                {maestriScelti().length > 0 && <span className="sid-scelta-staff__conta">{maestriScelti().length === 1 ? '1 maestro scelto' : `${maestriScelti().length} maestri scelti`}</span>}
                                                            </div>
                                                            {formData.sportTypes.length === 0 ? (
                                                                <div className="sid-scelta-staff__vuoto">Scegli prima lo sport: qui compaiono i maestri dello staff tecnico che lo insegnano.</div>
                                                            ) : filteredTeachers.length === 0 ? (
                                                                <div className="sid-scelta-staff__vuoto">{formData.sportTypes.length > 1 ? 'Nessun maestro dello staff tecnico insegna questi sport.' : 'Nessun maestro dello staff tecnico insegna questo sport.'}</div>
                                                            ) : (
                                                                <>
                                                                    <div id="lez-staff" tabIndex={-1} className="sid-scelta-staff__maestri">
                                                                        {filteredTeachers.map(t => {
                                                                            const scelto = maestriScelti().includes(t.value);
                                                                            return (
                                                                                <label key={t.value} htmlFor={`coach-${t.value}`} className={`sid-scelta-staff__maestro${scelto ? ' is-scelto' : ''}`}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        id={`coach-${t.value}`}
                                                                                        checked={scelto}
                                                                                        onChange={(e) => cambiaMaestro(t.value, e.target.checked)}
                                                                                    />
                                                                                    <span>{t.label}</span>
                                                                                </label>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                    <div className="sid-scelta-staff__aiuto">Scegli uno o più maestri: si crea una lezione per ciascuno.</div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col md={12}>
                                                    <Form.Label className="mb-2">Descrizione *</Form.Label>
                                                    <div id="lez-description" tabIndex={-1} className="mt-2 sid-editor">
                                                        <CKEditor
                                                            editor={ClassicEditor}
                                                            data={testoInHtml(formData.description)}
                                                            onChange={(evento, editor) => {
                                                                const description = editor.getData();
                                                                setFormData(prev => ({ ...prev, description }));
                                                            }}
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={12}>
                                                    <Form.Label>Foto *</Form.Label>
                                                    <div className="sid-photo-field">
                                                        <div className="sid-photo-field__preview">
                                                            {foto ? <img src={foto} alt="" /> : <span>Nessuna foto</span>}
                                                        </div>
                                                        <Button id="lez-photo" size="sm" variant="outline-dark" onClick={() => setPictureModalShowed(true)}>
                                                            {foto ? 'Sostituisci foto' : 'Carica foto'}
                                                        </Button>
                                                        {foto && (
                                                            <Button size="sm" variant="outline-danger" onClick={rimuoviFoto}>Rimuovi</Button>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>

                                            <h5 className="sid-form-section">Prezzi</h5>
                                            <Row className="g-4">
                                                <Col md={3}>
                                                    <Form.Label className="mb-2" htmlFor="lez-standard_price">Prezzo standard *</Form.Label>
                                                    <Form.Control
                                                        id="lez-standard_price"
                                                        disabled={prezzoBloccato}
                                                        className={`mt-2${prezzoBloccato ? ' sid-ro-input' : ''}`}
                                                        type="text"
                                                        inputMode="decimal"
                                                        name="standard_price"
                                                        value={formData.standard_price}
                                                        onChange={handleChange}
                                                    />
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Label className="mb-2">IVA</Form.Label>
                                                    <Select
                                                        className="mt-2"
                                                        styles={sidSelectStyles}
                                                        isDisabled={prezzoBloccato}
                                                        name="vat_rate"
                                                        value={findVatOption(formData)}
                                                        onChange={(selectedOption) => setFormData(prev => ({ ...prev, ...vatFieldsFromOption(selectedOption) }))}
                                                        options={VAT_OPTIONS}
                                                    />
                                                </Col>
                                                {showingTesseratiOptions && (
                                                    <Col md={3}>
                                                        <Form.Label className="mb-2" htmlFor="lez-federated_price">Prezzo tesserati / soci *</Form.Label>
                                                        <Form.Control id="lez-federated_price" className="mt-2" type="text" inputMode="decimal" name="federated_price" value={formData.federated_price} onChange={handleChange} />
                                                    </Col>
                                                )}
                                                <Col md={3}>
                                                    <Form.Label className="mb-2" htmlFor="lez-fitcoin_cashback">Cashback Fitcoin *</Form.Label>
                                                    <Form.Control id="lez-fitcoin_cashback" className="mt-2" type="number" min="0" step="1" name="fitcoin_cashback" value={formData.fitcoin_cashback} onChange={handleChange} />
                                                </Col>
                                            </Row>

                                            <h5 className="sid-form-section">Forme di pagamento</h5>
                                            <div className="sid-switch-inline">
                                                {interruttore("payable_in_club", "Acquisto pagabile in struttura")}
                                                {paymentMethods.stripe_enabled && interruttore("payable_stripe", "Acquisto pagabile su Stripe")}
                                                {paymentMethods.bank_transfer_enabled && interruttore("payable_bank", "Acquisto pagabile con bonifico")}
                                            </div>

                                            {/* «Non pubblicare in app» (TASK #9): la lezione non compare negli orari proposti dall'app */}
                                            <h5 className="sid-form-section">Visibilità</h5>
                                            <div className="sid-switch-inline">
                                                {interruttore("hidden_in_app", "Non pubblicare in app")}
                                            </div>
                                            {formData.hidden_in_app && <Form.Text className="d-block mt-2">{NOTA_NON_IN_APP}</Form.Text>}

                                            <h5 className="sid-form-section">Programmazione settimanale</h5>
                                            {formData.weeklySchedule.map((item, index) => (
                                                <Row key={item.id || index} className="g-4 mb-3 align-items-end">
                                                    <Col md={4}>
                                                        <Form.Label className="mb-2" htmlFor={`giorno-${index}`}>Giorno *</Form.Label>
                                                        <Select
                                                            inputId={`giorno-${index}`}
                                                            className="mt-2"
                                                            styles={sidSelectStyles}
                                                            placeholder=""
                                                            value={dayOptions.find(option => option.value === item.dayOfWeek) || null}
                                                            onChange={(selectedOption) => cambiaOrario(index, 'dayOfWeek', selectedOption?.value || '')}
                                                            options={dayOptions}
                                                        />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Label className="mb-2" htmlFor={`inizio-${index}`}>Ora inizio *</Form.Label>
                                                        <Form.Control id={`inizio-${index}`} className="mt-2" type="time" value={item.startTime} onChange={(e) => cambiaOrario(index, 'startTime', e.target.value)} />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Label className="mb-2" htmlFor={`fine-${index}`}>Ora fine *</Form.Label>
                                                        <Form.Control id={`fine-${index}`} className="mt-2" type="time" value={item.endTime} onChange={(e) => cambiaOrario(index, 'endTime', e.target.value)} />
                                                    </Col>
                                                    <Col md={2}>
                                                        <Button variant="outline-danger" size="sm" title="Rimuovi orario" onClick={async () => { if (await chiediConferma({ titolo: 'Rimuovere l\'orario?', testo: 'L\'orario sparisce dalla programmazione settimanale.' })) rimuoviOrario(index); }}>
                                                            <Trash size="16" />
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            ))}
                                            <Button id="lez-aggiungi-orario" variant="outline-dark" size="sm" onClick={aggiungiOrario}>
                                                <Add size="16" className="me-1" />
                                                Aggiungi orario
                                            </Button>
                                        </Card.Body>
                                    </Card>

                                    <div className={`d-flex ${bozzaEsistente ? 'justify-content-between' : 'justify-content-end'} align-items-center gap-4 mt-4`}>
                                        {bozzaEsistente && (
                                            <button type="button" className="btn btn-danger light" onClick={eliminaBozza}>Elimina bozza</button>
                                        )}
                                        <div className="d-flex gap-4">
                                            <button type="button" className="btn btn-dark light" onClick={() => navigate('/struttura/booking/lessons')}>
                                                Annulla
                                            </button>
                                            <button type="button" className="btn btn-secondary" onClick={salvaBozza}>Salva bozza</button>
                                            {!billingNotConfirmed && (
                                                <button type="button" className="btn btn-primary" onClick={salvaEAttiva}>Salva e attiva</button>
                                            )}
                                        </div>
                                    </div>
                                </Tab.Pane>
                            </Tab.Content>
                        </div>
                    </Row>
                </Tab.Container>
            </div>
        </>
    );
};
export default LessonsNew;
